import { useEffect, useState } from 'react';
import { useAuth } from '../../../../auth/Auth';
import { frontendLogger } from '../../../../utils/logger';
import { previewPrivateObject } from '../../../../utils/S3';

/**
 * Package hero-image attachments: fetch, delete, signed preview, snackbar.
 */
const usePackageImageAttachments = ({ packageId, initialImageUrl }) => {
    const { user, axiosInstance } = useAuth();
    const [attachments, setAttachments] = useState([]);
    const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState(false);
    const [imagePreviewUrl, setImagePreviewUrl] = useState(initialImageUrl || '');
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });

    useEffect(() => {
        if (initialImageUrl) {
            setImagePreviewUrl(initialImageUrl);
        }
    }, [initialImageUrl]);

    const getImageAttachment = () => {
        const imageAttachments = (attachments || []).filter(
            (a) => a.file_type && a.file_type.startsWith('image/')
        );
        if (!imageAttachments.length) return null;
        return [...imageAttachments].sort((a, b) => b.id - a.id)[0];
    };

    useEffect(() => {
        const attachment = getImageAttachment();
        if (!attachment) {
            setImagePreviewUrl('');
            return undefined;
        }

        let cancelled = false;
        const resolvePreview = async () => {
            try {
                if (!attachment.bucket_name || !attachment.storage_path) {
                    throw new Error('Attachment storage metadata is invalid for strict R2 download');
                }
                const url = await previewPrivateObject({ axiosInstance, attachmentId: attachment.id });
                if (!cancelled) setImagePreviewUrl(url || '');
            } catch (error) {
                console.error('Error previewing attachment:', error);
            }
        };

        resolvePreview();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attachments]);

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    const fetchAttachments = async () => {
        if (!packageId) return;
        try {
            const response = await axiosInstance.get('/attachments/', {
                params: { parent_table: 'trip_packages', parent_id: packageId },
            });
            setAttachments(response.data || []);
        } catch (error) {
            console.error('Error fetching attachments:', error);
            const errorMessage = error.response?.data?.detail ||
                error.response?.data?.message ||
                error.message ||
                'Error fetching attachments';

            setSnackbar({
                open: true,
                message: errorMessage,
                severity: 'error',
                autoHideDuration: 5000,
            });
        }
    };

    useEffect(() => {
        if (packageId) {
            fetchAttachments();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [packageId]);

    const handleDeleteAttachment = async (attachmentId) => {
        try {
            await axiosInstance.delete(`/attachments/${attachmentId}`);
            setAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
            setSnackbar({ open: true, message: 'Attachment deleted successfully!', severity: 'success' });
        } catch (error) {
            console.error('Error deleting attachment:', error);
            await frontendLogger.logError(error, {
                context: 'Attachment deletion',
                attachmentId,
                packageId,
                user: user?.username,
                action: 'delete',
                timestamp: new Date().toISOString(),
            });
            setSnackbar({ open: true, message: 'Error deleting attachment', severity: 'error' });
        }
    };

    return {
        user,
        axiosInstance,
        attachments,
        isAttachmentModalOpen,
        setIsAttachmentModalOpen,
        imagePreviewUrl,
        snackbar,
        setSnackbar,
        handleSnackbarClose,
        fetchAttachments,
        getImageAttachment,
        handleDeleteAttachment,
    };
};

export default usePackageImageAttachments;
